import { Languages } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useEditingLanguage } from '@/contexts/editing-language-context'
import { getVenueLanguageLabel } from '@/lib/venue-languages'
import { withServiceInstanceId } from '@/lib/service-instance'
import { cn } from '@/lib/utils'

type EditingLanguageSelectorProps = {
  triggerButtonClassName?: string
}

export function EditingLanguageSelector({ triggerButtonClassName }: EditingLanguageSelectorProps) {
  const {
    serviceInstanceId,
    supportedLanguages,
    primaryLanguage,
    editorLanguage,
    setEditorLanguage,
    loading,
  } = useEditingLanguage()

  if (!serviceInstanceId) {
    return null
  }

  const manageHref = withServiceInstanceId('/venue/admin/languages', serviceInstanceId)
  const languages = supportedLanguages.length ? supportedLanguages : [editorLanguage || 'en']

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'h-10 gap-1.5 rounded-lg border border-border px-2.5',
            triggerButtonClassName,
          )}
          title="Editing language for multilingual content"
          disabled={loading}
        >
          <Languages className="h-4 w-4 shrink-0" />
          <span className="hidden text-xs font-semibold tracking-wide sm:inline">Lang</span>
          <span className="text-xs font-semibold tracking-wide">
            {editorLanguage.toUpperCase()}
          </span>
          <span className="sr-only">Editing language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px]">
        <DropdownMenuLabel className="space-y-0.5">
          <div className="text-xs text-muted-foreground">Editing language</div>
          <div className="text-sm font-medium text-foreground">
            Applies to multilingual fields site-wide
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((code) => (
          <DropdownMenuItem
            key={code}
            onClick={() => setEditorLanguage(code)}
            className={cn(code === editorLanguage && 'bg-accent font-semibold')}
          >
            <span className="flex-1">{getVenueLanguageLabel(code)}</span>
            {code === primaryLanguage ? (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Primary</span>
            ) : null}
          </DropdownMenuItem>
        ))}
        {languages.length <= 1 ? (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              Add more languages to switch while editing.
            </div>
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to={manageHref}>Manage SI languages…</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
